import { LoadingState, QueryError } from '#/components/common/loading-state'
import { createFileRoute, Link } from '@tanstack/react-router'
import { AppHeader } from '#/components/layout/app-header'
import { ToolLogo } from '#/components/admin/tool-logo'
import { Button } from '#/components/ui/button'
import { usePublicTools, type PublicTool } from '#/lib/api-admin'
import { m } from '#/paraglide/messages'

export const Route = createFileRoute('/tools')({ component: Page, head: () => ({ meta: [{ title: `${m.tools_title()} · ${m.app_name()}` }] }) })

function Page() {
  const { data, isLoading, isError, refetch, isFetching } = usePublicTools()
  const tools = data?.tools ?? []
  return (
    <>
      <AppHeader />
      <main className="flex-1 overflow-y-auto px-6 py-10">
        <div className="mx-auto w-full max-w-4xl space-y-6">
          <div>
            <h1 className="text-3xl font-bold">{m.tools_title()}</h1>
            <p className="mt-1 text-muted-foreground">{m.tools_subtitle()}</p>
          </div>
          {isError ? <QueryError onRetry={refetch} retrying={isFetching} /> : isLoading ? (
            <LoadingState compact label={m.common_loading()} />
          ) : !tools.length ? (
            <div className="flex flex-col items-center gap-4 rounded-2xl border border-dashed px-6 py-16 text-center">
              <p className="max-w-md text-muted-foreground">{m.tools_empty()}</p>
              <Button asChild><Link to="/">{m.tools_back_home()}</Link></Button>
            </div>
          ) : (
            <ol className="space-y-4">{tools.map((t, i) => <ToolCard key={t.id} tool={t} rank={i + 1} />)}</ol>
          )}
        </div>
      </main>
    </>
  )
}

function ToolCard({ tool, rank }: { tool: PublicTool; rank: number }) {
  return (
    <li className="rounded-2xl border bg-card p-5">
      <div className="flex items-start gap-4">
        <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-semibold tabular-nums">#{rank}</div>
        {tool.logoUrl ? <ToolLogo src={tool.logoUrl} alt={tool.name} className="size-12 shrink-0 rounded-xl" /> : null}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="text-xl font-semibold">{tool.name}</h2>
            <div className="flex items-baseline gap-1">
              <span className="text-2xl font-bold tabular-nums">{tool.overallScore?.toFixed(1) ?? '—'}</span>
              <span className="text-xs text-muted-foreground">/ 10</span>
            </div>
          </div>
          {tool.description ? <p className="mt-1 text-sm text-muted-foreground">{tool.description}</p> : null}
          {tool.scores.length ? (
            <ul className="mt-4 space-y-2">
              {tool.scores.map((s) => (
                <li key={s.criteria} className="grid grid-cols-[140px_1fr_40px] items-center gap-3 text-sm">
                  <span className="truncate">{s.criteria}</span>
                  <span className="h-2 overflow-hidden rounded-full bg-muted" role="meter" aria-valuemin={0} aria-valuemax={10} aria-valuenow={s.score} aria-label={s.criteria}>
                    <span className="block h-full rounded-full bg-primary" style={{ width: `${Math.max(0, Math.min(100, s.score * 10))}%` }} />
                  </span>
                  <span className="text-right tabular-nums text-muted-foreground">{s.score.toFixed(1)}</span>
                  {s.note ? <span className="col-span-3 -mt-1 text-xs text-muted-foreground">{s.note}</span> : null}
                </li>
              ))}
            </ul>
          ) : null}
          {tool.videos.length ? (
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {tool.videos.map((v) => (
                <figure key={v.youtubeId} className="overflow-hidden rounded-xl border">
                  <iframe className="aspect-video w-full" src={`https://www.youtube-nocookie.com/embed/${v.youtubeId}`} title={v.title ?? tool.name} loading="lazy" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                  {v.title ? <figcaption className="px-3 py-2 text-xs text-muted-foreground">{v.title}</figcaption> : null}
                </figure>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </li>
  )
}
